import type { PixelEvent } from '@/events';

import {
  BaseDestinationAdapter,
  DEFAULT_COMMERCE_EVENT_TYPES,
} from './BaseDestinationAdapter';
import type { DestinationResult } from '../types';

export type MockDestinationOptions = {
  readonly failOnTypes?: ReadonlySet<string>;
  readonly ignoreTypes?: ReadonlySet<string>;
  readonly delayMs?: number;
  readonly supportedTypes?: ReadonlySet<string>;
};

/**
 * Controllable mock destination for unit tests (fail / ignore / delay).
 */
export class MockDestination extends BaseDestinationAdapter {
  private readonly failOnTypes: ReadonlySet<string>;

  private readonly ignoreTypes: ReadonlySet<string>;

  private readonly delayMs: number;

  private executions = 0;

  constructor(options: MockDestinationOptions = {}) {
    super({
      name: 'mock',
      version: '1.0.0',
      supportedTypes: options.supportedTypes ?? DEFAULT_COMMERCE_EVENT_TYPES,
    });
    this.failOnTypes = options.failOnTypes ?? new Set();
    this.ignoreTypes = options.ignoreTypes ?? new Set();
    this.delayMs = options.delayMs ?? 0;
  }

  async execute(context: {
    readonly event: PixelEvent;
    readonly signal: AbortSignal;
  }): Promise<DestinationResult> {
    const startedAt = Date.now();
    this.executions += 1;

    if (this.delayMs > 0) {
      await this.wait(this.delayMs, context.signal);
    }

    if (context.signal.aborted) {
      return this.createResult('failed', Date.now() - startedAt, {
        error: 'Aborted',
      });
    }

    if (this.ignoreTypes.has(context.event.type)) {
      return this.createResult('ignored', Date.now() - startedAt, {
        ignored: true,
      });
    }

    if (this.failOnTypes.has(context.event.type)) {
      throw new Error(`[Mock] Simulated failure for ${context.event.type}`);
    }

    return this.createResult('success', Date.now() - startedAt, {
      metadata: { executions: this.executions },
    });
  }

  getExecutionCount(): number {
    return this.executions;
  }

  private wait(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        resolve();
      }, ms);

      const onAbort = (): void => {
        cleanup();
        reject(new DOMException('The operation was aborted.', 'AbortError'));
      };

      const cleanup = (): void => {
        clearTimeout(timer);
        signal.removeEventListener('abort', onAbort);
      };

      if (signal.aborted) {
        cleanup();
        reject(new DOMException('The operation was aborted.', 'AbortError'));
        return;
      }

      signal.addEventListener('abort', onAbort);
    });
  }
}
